import { ProjectCreationWorkflow } from '../project-creation-workflow.model';

/**
 * The VIDA/DARES project-creation workflow (DT-6472; design FR-16, FR-29) — the first REAL
 * workflow registered in production.
 *
 * RULE keys only (the type is `keyof SpecificationFormRuleFields`, which carries `state`, never
 * `region`): the server patch key `region` corresponds to the rule key `state` — the two
 * vocabularies are deliberately different and must not be "kept matching".
 *
 * Overlay rationale (mirrors the design's ownership table):
 *   - `projectType`, `latitude`, `longitude`, `state` — VIDA-owned, shown READ-ONLY (the r12
 *     ruling: state arrives top-level when VIDA ships it, else from the first settlement);
 *   - `city` — NOT overlaid (owner ruling 2026-09-11): the base rule shows it, prefilled from
 *     the LGA at confirm but EDITABLE — city-from-LGA is our inference, not an REA fact. (Was
 *     `providedNull` before the ruling; the FR-8 LGA display still reads the link payload.)
 *   - `currency` + `exchangeRate` — hidden as a COUPLED PAIR (DARES subsidy is USD via the
 *     financials lock; the Tariff card does not apply);
 *   - `name` — shown READ-ONLY: stage one is program + slug ONLY (FR-29), then after confirm the
 *     name appears prefilled from the joined community names and LOCKED (REA standardization,
 *     2026-09-01). Not listed in `providerOwnedFields`: that hint means "VIDA sent this field",
 *     and the name is our join of the community names, not a field VIDA ships.
 */
/**
 * The workflow key on a link TRANSFERRED from REA's signed-projects register rather than confirmed
 * against VIDA (DT-6472, backfill).
 *
 * These projects predate the integration and have no VIDA application, so the link's `externalId`
 * names a SETTLEMENT. A key space of its own keeps a synthetic identifier from ever colliding with
 * a real `dares-nga-…` slug, and gives the UI something honest to branch its label on.
 */
export const VIDA_LEGACY_WORKFLOW_KEY = 'VIDA_LEGACY';

export const VIDA_WORKFLOW: ProjectCreationWorkflow = {
  key: 'VIDA',
  ruleOverlay: {
    // The name is REA-STANDARDIZED (2026-09-01), so it locks like any other provider-owned field.
    // Declared HERE rather than imperatively at confirm-time: the overlay is re-applied by
    // `applyFieldStates` on every path that builds the form, including `setFormValues` when an
    // existing project is reopened for editing. The original lock lived only in the component's
    // `onPrefillConfirmed`, which never runs for a saved project — so the field came back editable
    // behind "Edit" on the Project Design/Summary tab (Malin, 2026-09-30).
    name: { readOnly: true },
    projectType: { readOnly: true },
    latitude: { readOnly: true },
    longitude: { readOnly: true },
    state: { readOnly: true },
    currency: { display: false },
    exchangeRate: { display: false }
  },
  stageOneRuleOverlay: {
    name: { display: false, required: false }
  },
  providerOwnedFields: ['projectType', 'state', 'latitude', 'longitude'],
  providesProjectType: true,
  // DARES runs in one Nigeria program, but nothing stops a sponsor saving that program without a
  // country — and when that happened in dev the whole flow died silently (QA 2026-09-07).
  requiresProgramCountry: true,
  // DARES issues mini-grid and mesh-grid subsidies only; the tab shows exactly these tiles.
  entryProjectTypes: ['MINIGRID', 'MESHGRID']
};

/**
 * FR-29's name join: one community → its name verbatim; multiple → "A, B and C" (comma-separated,
 * "and" before the last). Pure so it runs under `npm run test:pure`.
 */
export const joinCommunityNames = (names: string[] | null | undefined): string => {
  if (!names || names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0];
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};
